import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { IconZoomIn, IconZoomOut } from '@tabler/icons-react';
import styles from './document.module.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface PDFPreviewProps {
  fileUrl: string;
  className?: string;
  onError?: (e: Error) => void;
  controlsPosition?: 'overlay' | 'bottom';
}

const PDFPreview: React.FC<PDFPreviewProps> = ({ fileUrl, className = '', onError, controlsPosition = 'overlay' }) => {
  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (containerRef.current) {
      setContainerWidth(containerRef.current.offsetWidth);
    }
  }, []);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
    setPageNumber(1);
  }

  const handlePrev = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const handleNext = () => setPageNumber(prev => (numPages ? Math.min(prev + 1, numPages) : prev));
  const handleZoomIn = () => setScale(s => Math.min(s + 0.2, 3));
  const handleZoomOut = () => setScale(s => Math.max(s - 0.2, 0.5));

  const isOverlay = controlsPosition === 'overlay';

  const paginationControls = numPages && numPages > 1 ? (
    <div className={styles.pdfPagination}>
      <button onClick={handlePrev} disabled={pageNumber <= 1} className={styles.paginationBtn}>Prev</button>
      <span className={styles.paginationText}>
        Page {pageNumber} of {numPages}
      </span>
      <button onClick={handleNext} disabled={pageNumber >= numPages} className={styles.paginationBtn}>Next</button>
    </div>
  ) : null;

  const zoomControls = (
    <div className={isOverlay ? styles.zoomOverlay : styles.zoomBottom}>
      <button
        className={scale > 0.5 ? styles.zoomBtn : styles.zoomBtnDisabled}
        onClick={handleZoomOut}
        aria-label="Zoom out"
        disabled={scale <= 0.5}
      >
        <IconZoomOut size={22} />
      </button>
      <span className={styles.zoomPercent}>{(scale * 100).toFixed(0)}%</span>
      <button
        className={scale < 3 ? styles.zoomBtn : styles.zoomBtnDisabled}
        onClick={handleZoomIn}
        aria-label="Zoom in"
        disabled={scale >= 3}
      >
        <IconZoomIn size={22} />
      </button>
    </div>
  );

  return (
    <div className={`w-full flex flex-col items-center ${styles.pdfPreview} ${className}`}>
      <div className={styles.pdfContainer} ref={containerRef}>
        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onError}
          loading={<span className="text-gray-400 text-base">Loading ...</span>}
          error={<span className="text-red-500 text-base">Failed to load.</span>}
        >
          {isOverlay && paginationControls}
          {isOverlay && zoomControls}
          <Page
            pageNumber={pageNumber}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            scale={scale}
            width={containerWidth || undefined}
          />
        </Document>
      </div>
      {!isOverlay && (
        <div className={styles.bottomControls}>
          {paginationControls}
          {zoomControls}
        </div>
      )}
    </div>
  );
};

export default PDFPreview;
